import React, { useState, useRef, forwardRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ptBR from 'date-fns/locale/pt-BR';
import { FaRegCalendarAlt, FaTimes } from 'react-icons/fa';
import ModalDetalhesEvento from '../modal/ModalDetalhesEvento';
import ModalInscritos from '../modal/ModalInscritos';
import ModalExperiencias from '../modal/ModalExperiencias';
import ModalConfirmacao from '../../modal/ModalConfirmacao';
import Snackbar from '../../common/Snackbar';
import EventosService from '../../../service/eventos/EventosService';
import { EstadoCarregando } from '../../home/tabela/EstadosTabela.js';

registerLocale('pt-BR', ptBR);

/**
 * Calcula o status real de exibição a partir do statusInscricao armazenado e dos
 * vagasDisponiveis de cada turno. Serve de fallback para eventos que tiveram
 * suas vagas preenchidas antes da lógica automática entrar em vigor.
 */
const derivarStatusEvento = (evento) => {
  const status = evento?.statusInscricao;
  const turnos = evento?.turnos;

  if (Array.isArray(turnos) && turnos.length > 0) {
    // Correção do NaN: pega o valor exato, se for nulo, usa as vagas totais como base
    const totalDisponiveis = turnos.reduce((acc, t) => {
        let disp = t.vagasDisponiveis;
        if (disp === undefined || disp === null) disp = t.vagas;
        return acc + (Number(disp) || 0);
    }, 0);

    // Se o banco diz que tá esgotado, mas a matemática diz que tem vaga (alguém cancelou), mostra Disponível!
    if (totalDisponiveis > 0 && status === 'esgotado') return 'disponivel';
    
    // Se a matemática diz que zerou, esgota!
    if (totalDisponiveis === 0 && status === 'disponivel') return 'esgotado';
  }

  return status;
};

const parseLocalEventDate = (dateInput) => {
  if (!dateInput) return null;
  const datePart = String(dateInput).substring(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

// Reusing Icons from Designacoes logic or generic SVGs
const TrashSvg = ({ onClick }) => (
  <svg width="19" height="26" viewBox="0 0 19 26" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={onClick} style={{ cursor: 'pointer' }}>
    <path d="M3.16602 7.39795H15.8327" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.91602 11.625V17.966" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M11.084 11.625V17.966" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M3.95898 7.39795L4.75065 20.08C4.75065 20.6406 4.91747 21.1782 5.2144 21.5746C5.51133 21.971 5.91406 22.1937 6.33398 22.1937H12.6673C13.0872 22.1937 13.49 21.971 13.7869 21.5746C14.0838 21.1782 14.2507 20.6406 14.2507 20.08L15.0423 7.39795" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7.125 7.39775V4.22725C7.125 3.94696 7.20841 3.67815 7.35687 3.47995C7.50534 3.28176 7.7067 3.17041 7.91667 3.17041H11.0833C11.2933 3.17041 11.4947 3.28176 11.6431 3.47995C11.7916 3.67815 11.875 3.94696 11.875 4.22725V7.39775" stroke="#DB1212" strokeWidth="1.57" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const EditSvg = ({ onClick }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" onClick={onClick} style={{ cursor: 'pointer' }}>
    <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#4A4A4A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="#4A4A4A" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const EncerrarInscricoesButton = ({ onClick, eventoNome }) => (
  <button
    type="button"
    title="Encerrar inscrições"
    aria-label={`Encerrar inscrições de ${eventoNome}`}
    onClick={onClick}
    style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#DB1212' }}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <rect x="8" y="8" width="8" height="8" rx="1" fill="currentColor" />
    </svg>
  </button>
);

const RetomarInscricoesButton = ({ onClick, eventoNome }) => (
  <button
    type="button"
    title="Retomar inscrições"
    aria-label={`Retomar inscrições de ${eventoNome}`}
    onClick={onClick}
    style={{ display: 'flex', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#00A868' }}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M10 8.5L16 12L10 15.5V8.5Z" fill="currentColor" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    </svg>
  </button>
);

const CalendarIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
);

const MapIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
);

// --- FUNÇÃO AUXILIAR PARA CORRIGIR URL DA IMAGEM ---
const getImageUrl = (path) => {
    if (!path) return '';
    const baseUrl = process.env.REACT_APP_API_URL || '';
    // Remove barra final da base e barra inicial do path para evitar // duplicada
    const cleanBase = baseUrl.replace(/\/$/, '');
    const cleanPath = path.replace(/^\//, '');
    return `${cleanBase}/${cleanPath}`;
};


const CardEventos = ({
  styles,
  eventos,
  loading,
  handleExcluir,
  handleEditar,
  onEventoAtualizado,
  eventosSelecionados,
  onToggleSelecionado
}) => {
  const isSecretaria = (() => {
    try { return JSON.parse(localStorage.getItem('perfilUsuario') || '[]').includes('secretaria'); } catch { return false; }
  })();
  const {
    Card,
    CardHeader,
    CardBody,
    EventoItem, // This will be the card-like item
  } = styles;

  const [modalAberto, setModalAberto] = useState(false);
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [modalInscritosAberto, setModalInscritosAberto] = useState(false);
  const [inscritosEvento, setInscritosEvento] = useState([]);
  const [modalExperienciasAberto, setModalExperienciasAberto] = useState(false);
  const [experienciasEvento, setExperienciasEvento] = useState([]);
  const [carregandoExperiencias, setCarregandoExperiencias] = useState(false);
  const [modalConfirmacaoAberto, setModalConfirmacaoAberto] = useState(false);
  const [eventoParaAbrirInscricoes, setEventoParaAbrirInscricoes] = useState(null);
  const [modalEncerrarInscricoesAberto, setModalEncerrarInscricoesAberto] = useState(false);
  const [eventoParaEncerrarInscricoes, setEventoParaEncerrarInscricoes] = useState(null);
  const [modalRetomarInscricoesAberto, setModalRetomarInscricoesAberto] = useState(false);
  const [eventoParaRetomarInscricoes, setEventoParaRetomarInscricoes] = useState(null);
  const [snackbar, setSnackbar] = useState({ aberto: false, mensagem: '', tipo: 'success' });
  const [filterDate, setFilterDate] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  // Cache local de confirmações enviadas por inscricaoId (persiste entre aberturas do modal)
  const confirmacoesCache = useRef(new Set());

  const CalendarIconButton = forwardRef(({ value, onClick }, ref) => (
    <FaRegCalendarAlt
      ref={ref}
      title={filterDate ? 'Alterar data do filtro' : 'Filtrar por data'}
      style={{ cursor: 'pointer', fontSize: 18, color: '#fff' }}
      onClick={onClick}
    />
  ));

  const mostrarSnackbar = (mensagem, tipo = 'success') => {
    setSnackbar({ aberto: true, mensagem, tipo });
  };

  const fecharSnackbar = () => {
    setSnackbar({ ...snackbar, aberto: false });
  };

  const handleVerDetalhes = (evento) => {
    setEventoSelecionado(evento);
    setModalAberto(true);
  };

  const handleVerInscritos = async (evento) => {
    setEventoSelecionado(evento);

    try {
      const detalhesEvento = await EventosService.getEventoPorId(evento._id);
      const frescos = detalhesEvento.inscritos || [];
      // Mescla cache local: se enviamos confirmação nesta sessão, preserva mesmo que DB ainda não retorne
      const merged = frescos.map(i =>
        confirmacoesCache.current.has(i.inscricaoId)
          ? { ...i, confirmacaoEnviada: true }
          : i
      );
      setInscritosEvento(merged);
    } catch (error) {
      console.error('Erro ao carregar inscritos:', error);
      mostrarSnackbar('Erro ao carregar inscritos.', 'error');
      setInscritosEvento(evento.inscritos || []);
    }

    setModalInscritosAberto(true);
  };

  const handleConfirmacaoEnviada = (inscricaoId) => {
    if (!inscricaoId) return;
    confirmacoesCache.current.add(inscricaoId);
    // Atualiza estado atual também para refletir imediatamente
    setInscritosEvento(prev =>
      prev.map(i => i.inscricaoId === inscricaoId ? { ...i, confirmacaoEnviada: true } : i)
    );
  };

  // Mock para experiências
  const handleVerExperiencias = async (evento) => {
    setEventoSelecionado(evento);
    setExperienciasEvento([]);
    setCarregandoExperiencias(true);
    setModalExperienciasAberto(true);
    
    try {
      const experiencias = await EventosService.getExperiencias(evento._id);
      setExperienciasEvento(Array.isArray(experiencias) ? experiencias : []);
    } catch (error) {
      console.error('Erro ao buscar experiências:', error);
      mostrarSnackbar('Erro ao carregar experiências.', 'error');
    } finally {
      setCarregandoExperiencias(false);
    }
  };

  const handleAbrirInscricoes = (eventoId) => {
    setEventoParaAbrirInscricoes(eventoId);
    setModalConfirmacaoAberto(true);
  };

  const confirmarAbertura = async () => {
    setModalConfirmacaoAberto(false);
    
    try {
      const EventosService = (await import('../../../service/eventos/EventosService')).default;
      await EventosService.updateEvento(eventoParaAbrirInscricoes, { statusInscricao: 'disponivel' });
      mostrarSnackbar('Inscrições abertas com sucesso!', 'success');
      setTimeout(() => {
        if (typeof onEventoAtualizado === 'function') {
          onEventoAtualizado();
        } else {
          window.location.reload();
        }
      }, 1500);
    } catch (error) {
      console.error('Erro ao abrir inscrições:', error);
      mostrarSnackbar('Erro ao abrir inscrições. Tente novamente.', 'error');
    }
  };

  const confirmarEncerramento = async () => {
    try {
      await EventosService.updateEvento(eventoParaEncerrarInscricoes, { statusInscricao: 'encerrado' });
      setModalEncerrarInscricoesAberto(false);
      setEventoParaEncerrarInscricoes(null);
      mostrarSnackbar('Inscrições encerradas com sucesso.', 'success');
      if (typeof onEventoAtualizado === 'function') {
        await onEventoAtualizado();
      }
    } catch (error) {
      console.error('Erro ao encerrar inscrições:', error);
      mostrarSnackbar('Erro ao encerrar inscrições. Tente novamente.', 'error');
    }
  };

  const confirmarRetomada = async () => {
    try {
      await EventosService.updateEvento(eventoParaRetomarInscricoes, { statusInscricao: 'disponivel' });
      setModalRetomarInscricoesAberto(false);
      setEventoParaRetomarInscricoes(null);
      mostrarSnackbar('Inscrições retomadas com sucesso.', 'success');
      if (typeof onEventoAtualizado === 'function') {
        await onEventoAtualizado();
      }
    } catch (error) {
      console.error('Erro ao retomar inscrições:', error);
      mostrarSnackbar('Erro ao retomar inscrições. Tente novamente.', 'error');
    }
  };

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const eventosFiltrados = (eventos || []).filter((evento) => {
    if (filterDate) {
      const dataEvento = parseLocalEventDate(evento.data);
      if (!dataEvento) return false;
      const mesmaData =
        dataEvento.getFullYear() === filterDate.getFullYear() &&
        dataEvento.getMonth() === filterDate.getMonth() &&
        dataEvento.getDate() === filterDate.getDate();

      if (!mesmaData) {
        return false;
      }
    }

    if (filterStatus) {
      const dataEvento = parseLocalEventDate(evento.data);
      const statusReal = derivarStatusEvento(evento);
      const encerrado = statusReal === 'encerrado' || (dataEvento && dataEvento < hoje);

      let statusDerivado;
      if (encerrado) {
        statusDerivado = 'encerrado';
      } else if (statusReal === 'em_breve') {
        statusDerivado = 'em_breve';
      } else if (statusReal === 'disponivel') {
        statusDerivado = 'disponivel';
      } else {
        statusDerivado = 'esgotado';
      }

      if (statusDerivado !== filterStatus) {
        return false;
      }
    }

    return true;
  });

  return (
    <>
      <Card>
        <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Eventos Cadastrados</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                backgroundColor: '#ffffff',
                color: '#008080',
                borderRadius: 8,
                border: 'none',
                fontSize: 12,
                padding: '4px 8px',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="">Status</option>
              <option value="disponivel">Disponível</option>
              <option value="em_breve">Em breve</option>
              <option value="encerrado">Encerrado</option>
              <option value="esgotado">Esgotado</option>
            </select>
            {filterDate && (
              <span style={{ fontSize: 12, color: '#f1f7f9' }}>
                {filterDate.toLocaleDateString('pt-BR')}
              </span>
            )}
            {filterDate && (
              <FaTimes
                title="Limpar filtro de data"
                style={{ cursor: 'pointer', fontSize: 16, color: '#f1f7f9' }}
                onClick={() => setFilterDate(null)}
              />
            )}
            <DatePicker
              selected={filterDate}
              onChange={(date) => setFilterDate(date)}
              customInput={<CalendarIconButton />}
              dateFormat="dd/MM/yyyy"
              locale="pt-BR"
              isClearable={false}
              popperPlacement="bottom-end"
              showPopperArrow={false}
              calendarStartDay={1}
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={15}
            />
          </div>
        </CardHeader>
        <CardBody>
          {loading ? (
            <EstadoCarregando />
          ) : eventosFiltrados && eventosFiltrados.length > 0 ? eventosFiltrados.map((evento) => (
            <div key={evento._id} style={{ 
                background: '#fff', 
                border: '1px solid #f0f0f0', 
                borderRadius: '12px', 
                padding: '24px 16px', 
                marginBottom: '0',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                height: '100%'
            }}>
                {/* Checkbox Top Left */}
                {!isSecretaria && (
                <div style={{ position: 'absolute', top: '18px', left: '18px' }}>
                    <input 
                        type="checkbox" 
                        checked={eventosSelecionados?.includes(evento._id) || false}
                        onChange={() => onToggleSelecionado && onToggleSelecionado(evento._id)}
                        style={{ transform: 'scale(1.3)', cursor: 'pointer', accentColor: '#008080' }} 
                    />
                </div>
                )}

                {/* Icons Action Top Right */}
                {!isSecretaria && (
                <div style={{ position: 'absolute', top: '18px', right: '18px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <TrashSvg onClick={() => handleExcluir(evento._id)} />
                  <EditSvg onClick={() => handleEditar(evento)} />
                  {evento.statusInscricao === 'encerrado' ? (
                    <RetomarInscricoesButton
                      eventoNome={evento.nome || evento.titulo}
                      onClick={() => {
                        setEventoParaRetomarInscricoes(evento._id);
                        setModalRetomarInscricoesAberto(true);
                      }}
                    />
                  ) : (
                    <EncerrarInscricoesButton
                      eventoNome={evento.nome || evento.titulo}
                      onClick={() => {
                        setEventoParaEncerrarInscricoes(evento._id);
                        setModalEncerrarInscricoesAberto(true);
                      }}
                    />
                  )}
                </div>
                )}

                {/* Image */}
                <div style={{ width: '70px', height: '70px', background: '#e2e8f0', borderRadius: '50%', marginBottom: '14px', overflow: 'hidden', border: '2px solid #fff', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}>
                  {evento.imagemUrl ? (
                    <img 
                      src={getImageUrl(evento.imagemUrl)}//ALTERADO 
                      alt={evento.nome} 
                      crossOrigin="anonymous"//ADICIONADO
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => {
                        console.error('Erro ao carregar imagem:', e.target.src);
                        console.error('Status da resposta:', e.target.naturalHeight, e.target.naturalWidth);
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<span style="display:flex; width:100%; height:100%; align-items:center; justify-content:center; fontSize:10px; color:#64748b">IMG</span>';
                      }}
                      onLoad={(e) => {
                        console.log('Imagem carregada com sucesso:', e.target.src);
                      }}
                    />
                  ) : (
                    <span style={{ display:'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent:'center', fontSize: '10px', color: '#64748b' }}>IMG</span>
                  )}
                </div>

                {/* Title */}
                <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 'bold', color: '#222', textAlign: 'center' }}>{evento.nome || evento.titulo}</h3>

                {/* Date */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#555', marginBottom: '6px' }}>
                  <CalendarIcon />
                  <span style={{ paddingTop: '2px' }}>{(parseLocalEventDate(evento.data) || new Date(evento.data)).toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>

                {/* Location */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '13px', color: '#555', marginBottom: '16px', textAlign: 'center', maxWidth: '90%' }}>
                  <div style={{ marginTop: '2px' }}><MapIcon /></div>
                  <span style={{ lineHeight: '1.4' }}>{evento.local ? evento.local.split('|')[0].trim() : ''}</span>
                </div>

                {/* Status Logic */}
                {(() => {
                  const hoje = new Date();
                  hoje.setHours(0, 0, 0, 0);
                  const dataEvento = parseLocalEventDate(evento.data);
                  const statusReal = derivarStatusEvento(evento);
                  const encerrado = statusReal === 'encerrado' || (dataEvento && dataEvento < hoje);

                  // Button Base Style
                  const btnBase = {
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: '600',
                    height: '34px',
                    color: 'white',
                    transition: 'opacity 0.2s'
                  };
                  
                  if (encerrado) {
                    return <>
                      <div style={{ background: '#F3F4F6', color: '#444', padding: '4px 16px', borderRadius: '20px', fontSize: '13px', marginBottom: '16px', fontWeight: '600' }}>
                        Encerrado
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="evento-card-button" onClick={() => handleVerExperiencias(evento)} style={{ ...btnBase, background: '#117D77' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                          Experiências
                        </button>
                        <button className="evento-card-button" onClick={() => handleVerInscritos(evento)} style={{ ...btnBase, background: '#00A868' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                          Ver Inscritos
                        </button>
                      </div>
                    </>;
                  } else if (statusReal === 'em_breve') {
                    return <>
                      <div style={{ background: '#feefc3', color: '#b45309', padding: '4px 16px', borderRadius: '20px', fontSize: '13px', marginBottom: '16px', fontWeight: '600' }}>Em Breve!</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="evento-card-button" onClick={() => handleVerDetalhes(evento)} style={{ ...btnBase, background: '#117D77' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                          Ver Detalhes
                        </button>
                        <button className="evento-card-button" onClick={() => handleAbrirInscricoes(evento._id)} style={{ ...btnBase, background: '#2563EB' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                          Abrir Inscrições
                        </button>
                      </div>
                    </>;
                  } else if (statusReal === 'disponivel') {
                    return <>
                      <div style={{ background: '#bef2d8', color: '#047857', padding: '4px 16px', borderRadius: '20px', fontSize: '13px', marginBottom: '16px', fontWeight: '600' }}>Disponível</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="evento-card-button" onClick={() => handleVerDetalhes(evento)} style={{ ...btnBase, background: '#117D77' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                          Ver Detalhes
                        </button>
                        <button className="evento-card-button" onClick={() => handleVerInscritos(evento)} style={{ ...btnBase, background: '#00A868' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                          Ver Inscritos
                        </button>
                      </div>
                    </>;
                  } else {
                    return <>
                      <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '4px 16px', borderRadius: '20px', fontSize: '13px', marginBottom: '16px', fontWeight: '600' }}>Esgotado</div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="evento-card-button" onClick={() => handleVerDetalhes(evento)} style={{ ...btnBase, background: '#117D77' }}>
                           <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                          Ver Detalhes
                        </button>
                        <button className="evento-card-button" onClick={() => handleVerInscritos(evento)} style={{ ...btnBase, background: '#00A868' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                          Ver Inscritos
                        </button>
                      </div>
                    </>;
                  }
                })()}
            </div>
              )) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  {filterDate || (filterStatus && filterStatus !== 'todos')
                    ? 'Nenhum evento encontrado com os filtros selecionados.'
                    : 'Nenhum evento cadastrado.'}
                </div>
              )}
        </CardBody>
        <div style={{ marginBottom: '50px' }}></div>
      </Card>
      <ModalDetalhesEvento
        isOpen={modalAberto}
        onClose={() => setModalAberto(false)}
        evento={eventoSelecionado}
      />
      
      <ModalConfirmacao
        isOpen={modalConfirmacaoAberto}
        onClose={() => setModalConfirmacaoAberto(false)}
        onConfirm={confirmarAbertura}
        mensagem="Deseja abrir as inscrições para este evento?"
      />

      <ModalConfirmacao
        isOpen={modalEncerrarInscricoesAberto}
        onClose={() => {
          setModalEncerrarInscricoesAberto(false);
          setEventoParaEncerrarInscricoes(null);
        }}
        onConfirm={confirmarEncerramento}
        mensagem="Deseja encerrar as inscrições para este evento?"
      />

      <ModalConfirmacao
        isOpen={modalRetomarInscricoesAberto}
        onClose={() => {
          setModalRetomarInscricoesAberto(false);
          setEventoParaRetomarInscricoes(null);
        }}
        onConfirm={confirmarRetomada}
        mensagem="Deseja retomar as inscrições para este evento?"
      />
      
      {snackbar.aberto && (
        <Snackbar 
          mensagem={snackbar.mensagem} 
          tipo={snackbar.tipo} 
          onClose={fecharSnackbar} 
        />
      )}
      
      <ModalInscritos
        isOpen={modalInscritosAberto}
        onClose={() => setModalInscritosAberto(false)}
        inscritos={inscritosEvento}
        evento={eventoSelecionado}
        onConfirmacaoEnviada={handleConfirmacaoEnviada}
      />
      <ModalExperiencias
        isOpen={modalExperienciasAberto}
        onClose={() => setModalExperienciasAberto(false)}
        experiencias={experienciasEvento}
        carregando={carregandoExperiencias}
      />
    </>
  );
};

export default CardEventos;